'use client';

import React, { useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PrimaryButton, SecondaryButton } from '@/components/platform/buttons';
import { Input } from '@/components/platform/forms';
import Alert from '@/components/platform/ui/Alert';
import Chip from '@/components/platform/ui/Chip';
import { logger } from '@/lib/platform/utils/logger';

type ScopeType = 'club' | 'valuations' | 'player_sales';
type OperationType = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: {
    name: string;
    description: string;
    scopes: ScopeType[];
    operations: OperationType[];
  }) => Promise<string>; // Returns the generated API key
  organizationId: string;
  existingNames?: string[]; // List of existing API key names for duplicate checking
}

export default function CreateApiKeyModal({
  isOpen,
  onClose,
  onCreate,
  organizationId,
  existingNames = [],
}: CreateApiKeyModalProps) {
  const { darkMode } = useDarkMode();
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<ScopeType[]>([]);
  const [selectedOperations, setSelectedOperations] = useState<OperationType[]>(['GET']); // Default to GET
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedApiKey, setGeneratedApiKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [nameWarning, setNameWarning] = useState('');

  const scopeOptions: { id: ScopeType; label: string; description: string }[] = [
    { id: 'club', label: 'Club', description: 'Access to club name, address, and base currency' },
    { id: 'valuations', label: 'Valuations', description: 'Access to valuation data' },
    { id: 'player_sales', label: 'Player Sales', description: 'Access to player sales data' },
  ];

  const operationOptions: {
    id: OperationType;
    label: string;
    description: string;
    color: 'blue' | 'green' | 'yellow' | 'red';
  }[] = [{ id: 'GET', label: 'GET', description: 'Read-only access', color: 'blue' }];

  const handleScopeToggle = (scopeId: ScopeType) => {
    if (selectedScopes.includes(scopeId)) {
      setSelectedScopes(selectedScopes.filter((s) => s !== scopeId));
    } else {
      setSelectedScopes([...selectedScopes, scopeId]);
    }
  };

  const handleNameChange = (newName: string) => {
    setName(newName);
    // Check for duplicate name
    if (newName.trim() && existingNames.includes(newName.trim())) {
      setNameWarning('An API key with this name already exists');
    } else {
      setNameWarning('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter a name for the API key');
      return;
    }

    // Check for duplicate name before submitting
    if (existingNames.includes(name.trim())) {
      setError('An API key with this name already exists. Please choose a different name.');
      return;
    }

    if (selectedScopes.length === 0) {
      setError('Please select at least one scope');
      return;
    }

    if (selectedOperations.length === 0) {
      setError('Please select at least one operation');
      return;
    }

    setLoading(true);
    try {
      const apiKey = await onCreate({
        name: name.trim(),
        description: '',
        scopes: selectedScopes,
        operations: selectedOperations,
      });

      setGeneratedApiKey(apiKey);
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset all state
      setStep('form');
      setName('');
      setSelectedScopes([]);
      setSelectedOperations(['GET']);
      setError('');
      setNameWarning('');
      setGeneratedApiKey('');
      setCopied(false);
      setCopiedUrl(null);
      onClose();
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedApiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('Failed to copy:', err);
    }
  };

  const copyFullUrl = async (endpoint: string) => {
    try {
      const fullUrl = `https://valiyou.io/api/v1/${endpoint}?api_key=${generatedApiKey}&organization_id=${organizationId}`;
      await navigator.clipboard.writeText(fullUrl);
      setCopiedUrl(endpoint);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      logger.error('Failed to copy URL:', err);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/75 backdrop-blur-sm transition-opacity duration-300 ease-out data-closed:opacity-0"
      />

      <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className={`relative w-full max-w-lg transform overflow-hidden rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transition-all duration-300 ease-out data-closed:scale-95 data-closed:opacity-0 sm:p-6 ${
              darkMode ? 'border border-white/10 bg-gray-800' : 'bg-white'
            }`}
          >
            {/* Close Button */}
            <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className={`rounded-md focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden ${
                  darkMode
                    ? 'bg-gray-800 text-gray-400 hover:text-gray-300'
                    : 'bg-white text-gray-400 hover:text-gray-500'
                }`}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>

            {/* Header */}
            <div className="mb-6">
              <DialogTitle
                className={`text-base font-semibold ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}
              >
                {step === 'form' ? 'Create API Key' : 'API Key Created'}
              </DialogTitle>
              <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {step === 'form'
                  ? 'Create a new API key for external integrations'
                  : "Copy your API key now - you won't be able to see it again!"}
              </p>
            </div>

            {step === 'form' ? (
              <form onSubmit={handleSubmit}>
                {/* Name Input */}
                <div className="mb-6">
                  <Input
                    label="Name"
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="e.g., Power BI Integration"
                  />
                  {/* Duplicate Name Warning */}
                  {nameWarning && (
                    <div
                      className={`mt-2 flex items-start gap-2 rounded-md p-3 ${
                        darkMode
                          ? 'border border-yellow-500/20 bg-yellow-500/10'
                          : 'border border-yellow-200 bg-yellow-50'
                      }`}
                    >
                      <svg
                        className={`mt-0.5 size-5 shrink-0 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className={`text-sm ${darkMode ? 'text-yellow-400' : 'text-yellow-800'}`}>
                        {nameWarning}
                      </p>
                    </div>
                  )}
                </div>

                {/* Scopes & Operations Selection */}
                <div className="mb-6">
                  <label
                    className={`mb-3 block text-sm font-medium ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}
                  >
                    Scopes &amp; Operations
                  </label>

                  <div className="overflow-hidden rounded-lg">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr
                          className={`border-b ${
                            darkMode
                              ? 'border-white/10 bg-white/[0.03]'
                              : 'border-gray-200 bg-gray-50'
                          }`}
                        >
                          <th className="w-10 p-2"></th>
                          <th
                            className={`p-2 text-left text-xs font-semibold tracking-wider uppercase ${
                              darkMode ? 'text-gray-400' : 'text-gray-600'
                            }`}
                          >
                            Scope
                          </th>
                          <th
                            className={`p-2 text-left text-xs font-semibold tracking-wider uppercase ${
                              darkMode ? 'text-gray-400' : 'text-gray-600'
                            }`}
                          >
                            Description
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {scopeOptions.map((scope, index) => {
                          const isSelected = selectedScopes.includes(scope.id);
                          return (
                            <tr
                              key={scope.id}
                              onClick={() => handleScopeToggle(scope.id)}
                              className={`cursor-pointer transition-colors ${
                                darkMode ? 'hover:bg-white/[0.03]' : 'hover:bg-gray-50/50'
                              } ${
                                index < scopeOptions.length - 1
                                  ? darkMode
                                    ? 'border-b border-white/[0.06]'
                                    : 'border-b border-gray-100'
                                  : ''
                              }`}
                            >
                              <td className="p-2 text-center">
                                <div
                                  className={`mx-auto flex size-5 items-center justify-center rounded-md border transition-all ${
                                    isSelected
                                      ? darkMode
                                        ? 'border-white/10 bg-white/[0.12] text-gray-100'
                                        : 'border-gray-300 bg-gray-200 text-gray-900'
                                      : darkMode
                                        ? 'border-white/10 bg-white/[0.05] text-transparent'
                                        : 'border-gray-200 bg-gray-50 text-transparent'
                                  }`}
                                >
                                  {isSelected && (
                                    <CheckIcon className="size-3.5" strokeWidth={2.5} />
                                  )}
                                </div>
                              </td>
                              <td
                                className={`p-2 text-sm font-medium ${
                                  darkMode ? 'text-gray-100' : 'text-gray-900'
                                }`}
                              >
                                {scope.label}
                              </td>
                              <td
                                className={`p-2 text-xs ${
                                  darkMode ? 'text-gray-400' : 'text-gray-600'
                                }`}
                              >
                                {scope.description}
                              </td>
                            </tr>
                          );
                        })}

                        {/* Operations Section Header */}
                        <tr className={darkMode ? 'bg-white/[0.03]' : 'bg-gray-100'}>
                          <td
                            colSpan={3}
                            className={`p-2 text-xs font-semibold tracking-wider uppercase ${
                              darkMode ? 'text-gray-400' : 'text-gray-600'
                            }`}
                          >
                            Operations
                          </td>
                        </tr>

                        {operationOptions.map((operation, index) => {
                          return (
                            <tr
                              key={operation.id}
                              className={`${
                                index < operationOptions.length - 1
                                  ? darkMode
                                    ? 'border-b border-white/[0.06]'
                                    : 'border-b border-gray-100'
                                  : ''
                              }`}
                            >
                              <td className="p-2 text-center">
                                <div
                                  className={`mx-auto flex size-5 items-center justify-center rounded-md border transition-all ${
                                    darkMode
                                      ? 'border-white/10 bg-white/[0.12] text-gray-100'
                                      : 'border-gray-300 bg-gray-200 text-gray-900'
                                  }`}
                                >
                                  <CheckIcon className="size-3.5" strokeWidth={2.5} />
                                </div>
                              </td>
                              <td className={`p-2`}>
                                <Chip color={operation.color}>{operation.label}</Chip>
                              </td>
                              <td
                                className={`p-2 text-xs ${
                                  darkMode ? 'text-gray-400' : 'text-gray-600'
                                }`}
                              >
                                {operation.description}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-4">
                    <Alert type="error" message={error} onDismiss={() => setError('')} />
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <SecondaryButton
                    type="button"
                    onClick={handleClose}
                    disabled={loading}
                    size="lg"
                    className="rounded-full"
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    type="submit"
                    disabled={loading}
                    size="lg"
                    className="rounded-full"
                  >
                    {loading ? 'Creating...' : 'Create API Key'}
                  </PrimaryButton>
                </div>
              </form>
            ) : (
              /* Step 2: Success - Show API Key */
              <div>
                {/* Warning Banner */}
                <div
                  className={`mb-6 rounded-lg p-4 ${
                    darkMode
                      ? 'border border-yellow-500/20 bg-yellow-500/10'
                      : 'border border-yellow-200 bg-yellow-50'
                  }`}
                >
                  <div className="flex">
                    <div className="shrink-0">
                      <svg
                        className={`size-5 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3
                        className={`text-sm font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-800'}`}
                      >
                        Important: Save this API key now
                      </h3>
                      <div
                        className={`mt-1 text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-700'}`}
                      >
                        <p>
                          This is the only time you&apos;ll be able to view this API key. Make sure
                          to copy it and store it securely.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* API Key Display */}
                <div className="mb-6">
                  <label
                    className={`mb-2 block text-sm font-medium ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}
                  >
                    Your API Key
                  </label>
                  <div
                    className={`relative rounded-lg border ${
                      darkMode ? 'border-white/10 bg-gray-900' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 pr-2">
                      <code
                        className={`flex-1 px-4 py-3 font-mono text-sm break-all ${
                          darkMode ? 'text-green-400' : 'text-gray-900'
                        }`}
                      >
                        {generatedApiKey}
                      </code>
                      <button
                        onClick={copyToClipboard}
                        className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                          copied
                            ? darkMode
                              ? 'bg-green-600 text-white'
                              : 'bg-green-600 text-white'
                            : darkMode
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  {/* Copy Full URL Buttons - Based on Selected Scopes */}
                  {selectedScopes.length > 0 && (
                    <div className="mt-3">
                      <label
                        className={`mb-2 block text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                      >
                        Quick Copy URLs:
                      </label>
                      <div className="flex gap-2">
                        {selectedScopes.includes('club') && (
                          <button
                            onClick={() => copyFullUrl('club')}
                            className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                              copiedUrl === 'club'
                                ? darkMode
                                  ? 'border-green-600 bg-green-600 text-white'
                                  : 'border-green-600 bg-green-600 text-white'
                                : darkMode
                                  ? 'hover:bg-gray-650 border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
                                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {copiedUrl === 'club' ? '✓' : 'Club'}
                          </button>
                        )}
                        {selectedScopes.includes('valuations') && (
                          <button
                            onClick={() => copyFullUrl('valuations')}
                            className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                              copiedUrl === 'valuations'
                                ? darkMode
                                  ? 'border-green-600 bg-green-600 text-white'
                                  : 'border-green-600 bg-green-600 text-white'
                                : darkMode
                                  ? 'hover:bg-gray-650 border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
                                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {copiedUrl === 'valuations' ? '✓' : 'Valuations'}
                          </button>
                        )}
                        {selectedScopes.includes('player_sales') && (
                          <button
                            onClick={() => copyFullUrl('player-sales')}
                            className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                              copiedUrl === 'player-sales'
                                ? darkMode
                                  ? 'border-green-600 bg-green-600 text-white'
                                  : 'border-green-600 bg-green-600 text-white'
                                : darkMode
                                  ? 'hover:bg-gray-650 border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
                                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {copiedUrl === 'player-sales' ? '✓' : 'Player Sales'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Key Details */}
                <div
                  className={`mb-6 rounded-lg p-4 ${darkMode ? 'bg-white/[0.03]' : 'bg-gray-50'}`}
                >
                  <h4
                    className={`mb-3 text-sm font-medium ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}
                  >
                    API Key Details
                  </h4>
                  <dl className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <dt className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Name:</dt>
                      <dd className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {name}
                      </dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Scopes:</dt>
                      <dd className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {selectedScopes.join(', ')}
                      </dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Operations:</dt>
                      <dd className="flex gap-1">
                        {selectedOperations.map((op) => {
                          const opColor =
                            operationOptions.find((o) => o.id === op)?.color || 'gray';
                          return (
                            <Chip key={op} color={opColor}>
                              {op}
                            </Chip>
                          );
                        })}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Actions */}
                <div className="flex justify-end">
                  <SecondaryButton
                    type="button"
                    onClick={handleClose}
                    size="lg"
                    className="rounded-full"
                  >
                    Done
                  </SecondaryButton>
                </div>
              </div>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
